import {
	finalize, Observable, of, tap,
} from 'rxjs';

import {
	AuthService, Container, i18n, Service,
} from '../../../../symphony';

import { getInitialServerSideData } from '../../../../shared/public/SlUtils/getInitialServerSideData';
import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import {
	HOT_TODAY_PROJECTS_COUNT,
	CODE_BITS_LIST_COUNT,
	orderFilerTypeAlias,
	SlCodeBitsContext,
} from '../../global-constants';
import {
	ICodeBitsDetailsResponse, CodeBitsSearchFilter, ILanguagesCatalogResponse, ILanguage,
} from '../../../../api/public/code-api';
import { DiscussSearchFilter } from '../../../../api/public/discuss-api';

@Service()
export class SlCodeBitsService {
	private api = Container.take(SlCodeBitsContext, SlApiService);

	private dataService = Container.take(SlCodeBitsContext, SlDataService);

	private authService = Container.take('global', AuthService);

	public getHotTodayProjects = (): Observable<ICodeBitsDetailsResponse> => {
		const ssrHotTodayProjects = getInitialServerSideData(
			'ssrHotTodayProjects',
		);

		if (ssrHotTodayProjects) {
			this.dataService.hotTodayProjects$.next(ssrHotTodayProjects);

			return of(null);
		}

		return this.api
			.getCodeBitsDetails({
				count: HOT_TODAY_PROJECTS_COUNT,
				filter: CodeBitsSearchFilter.HotToday,
				index: 0,
				query: '',
			})
			.pipe(
				tap((hotTodayTopics) => {
					this.dataService.hotTodayProjects$.next(
						hotTodayTopics.projects,
					);
				}),
			);
	};

	public getCodeBitsList = (
		codeBitsListSearchText: string,
		orderFilter: string,
		currentPageNumber: number,
		languageFilter: string,
	): Observable<ICodeBitsDetailsResponse> => {
		const ssrCodeBitsList = getInitialServerSideData('ssrCodeBitsList');
		const ssrCodeBitsListCount = getInitialServerSideData(
			'ssrCodeBitsListCount',
		);
		const filter = DiscussSearchFilter[
            	Object.keys(orderFilerTypeAlias).find(
            		(key) => orderFilerTypeAlias[key] === orderFilter,
			)
		];

		if (ssrCodeBitsList) {
			this.dataService.codeBitsList$.next(ssrCodeBitsList);
			this.dataService.codeBitsListCount$.next(ssrCodeBitsListCount);

			return of(null);
		}

		this.dataService.isCodeBitsListLoading$.next(true);
		return this.api
			.getCodeBitsDetails({
				count: CODE_BITS_LIST_COUNT,
				filter,
				language: languageFilter === 'all' ? null : languageFilter,
				index: CODE_BITS_LIST_COUNT * (currentPageNumber - 1),
				query: codeBitsListSearchText,
				profileId: this.authService.getUser()?.id || 0,
			})
			.pipe(
				tap((res) => {
					this.dataService.codeBitsList$.next(res.projects);
					this.dataService.codeBitsListCount$.next(res.count);
				}),
				finalize(() => {
					this.dataService.isCodeBitsListLoading$.next(false);
				}),
			);
	};

	private generateSupportedLanguages = (
		res: ILanguagesCatalogResponse,
	): ILanguage[] => {
		const languages = res.data.reduce((acc, item) => [...acc, ...item.languageDetails], []);

		return [
			{
				key: 'all',
				displayName: i18n.t('web-code.select-language-all-languages'),
				color: '',
				name: 'all',
			},
			...languages,
		];
	};

	public getLanguagesCatalog = (): Observable<ILanguagesCatalogResponse> => {
		const ssrSupportedLanguages = getInitialServerSideData(
			'ssrSupportedLanguages',
		);

		if (ssrSupportedLanguages) {
			this.dataService.supportedLanguages$.next(
				this.generateSupportedLanguages(ssrSupportedLanguages),
			);
			return of(null);
		}

		return this.api.getLanguagesCatalog().pipe(
			tap((res) => {
				this.dataService.supportedLanguages$.next(
					this.generateSupportedLanguages(res),
				);
			}),
		);
	};
}
